/* eslint-disable react-hooks/exhaustive-deps */
import { ButtonPrimary } from '@/components/button'
import { Close } from '@/components/icons'
import { useBracketRefund, useGetPlayerRegisteredBrackets } from '@/services'
import { Dialog, Transition } from '@headlessui/react'
import { isEmpty } from 'lodash'
import React, { Fragment, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Step1 } from './Step1'
import { Step2 } from './Step2'
import { Step3 } from './Step3'

type Props = {
  open: boolean
  onClose: () => void
  currentPlayerId?: number
  currentEventId?: string
  currentPlayer: string
  currentClubId: number
  refundCallBack?: (playerId?: number) => void
}

type FormValues = {
  options: string
  prevOptions?: string
  // total: number
  // brackets: any
  // clubId: number
  // eventId: number
  // playerId: number
  // playerName: string
  // processRefund: boolean
  // refundAmount: number
  // currencySymbol: string
  // eventChecked: boolean
}

export const WithdrawRefundModal = ({
  open,
  onClose,
  currentPlayerId,
  currentEventId,
  currentPlayer,
  currentClubId,
  refundCallBack,
}: Props) => {
  const { t } = useTranslation()

  const [activeStep, setActiveStep] = useState(0)

  const methods = useForm<FormValues>({
    defaultValues: {
      options: '',
      prevOptions: '',
      // total: 0,
      // brackets: [],
      // clubId: 0,
      // eventId: 0,
      // playerId: 0,
      // playerName: '',
      // processRefund: false,
      // refundAmount: 0,
      // currencySymbol: '',
      // eventChecked: false,
    },
  })

  const { handleSubmit, trigger, getValues, reset } = methods

  const { data: bracketList } = useGetPlayerRegisteredBrackets(currentEventId, currentPlayerId)
  const { mutate: refundBrackets } = useBracketRefund()

  const handleNext = async () => {
    const isStepValid = await trigger()
    if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = (data: any) => {
    const updatedBrackets = data.brackets.filter((el: any) => el.checked === true && el.type === 'bracket')
    const updatedEvent = data.brackets.filter((el: any) => el.type === 'event')
    const array: any = []
    updatedBrackets.forEach((element: any) => {
      const obj = {
        bracketId: element.bracketId,
        bracketName: element.name,
        registrationId: element.registrationDetails.registrationId,
        withdrawPlayer: element.withdrawPlayer,
        processRefund: Number(element.refund) !== 0 ? element.processRefund : false,
        refundAmount: Number(element.refund),
      }
      array.push(obj)
    })
    const payload = {
      clubId: currentClubId,
      eventId: currentEventId,
      eventName: updatedEvent?.[0]?.name,
      playerId: currentPlayerId,
      processRefund: isEmpty(updatedEvent)
        ? false
        : Number(updatedEvent?.[0]?.refund) !== 0
          ? updatedEvent?.[0]?.processRefund
          : false,
      refundAmount: isEmpty(updatedEvent) ? 0 : Number(updatedEvent?.[0]?.refund),
      brackets: array,
    }
    refundBrackets(payload, {
      onSuccess: () => {
        toast.success(t('common.success'))
        if (refundCallBack) refundCallBack(currentPlayerId)
        onClose()
      },
      onError: () => {
        toast.error('Error')
        onClose()
      },
    })
  }

  const steps = ['Withdraw/Refund', getValues('options'), 'Confirmation']

  useEffect(() => {
    if (open) {
      setActiveStep(0)
      reset()
    }
  }, [open])

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <Step1 />
      case 1:
        return <Step2 bracketList={bracketList?.results} currentPlayer={currentPlayer} currentClubId={currentClubId} />
      case 2:
        return <Step3 currentPlayer={currentPlayer} />
      case 3:
      default:
        return 'Unknown step'
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <FormProvider {...methods}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full flex flex-col gap-y-8 ${
                    activeStep === 1 ? 'max-w-3xl' : 'max-w-sm'
                  } transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative`}
                >
                  <button
                    className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                    onClick={handleClose}
                  >
                    <Close className="text-navy-900" />
                  </button>

                  {steps.map((label, index) => (
                    <React.Fragment key={label}>
                      {activeStep === index && (
                        <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                          {label}
                        </Dialog.Title>
                      )}
                    </React.Fragment>
                  ))}

                  {getStepContent(activeStep)}

                  {activeStep === 0 && (
                    <div className="flex justify-center gap-x-6">
                      <button
                        onClick={handleClose}
                        className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 py-1 w-24 rounded text-navy-900 font-semibold"
                      >
                        {t('common.cancel')}
                      </button>
                      <ButtonPrimary title={t('common.next')} className="!py-1 !w-24 !rounded" onClick={handleNext} />
                    </div>
                  )}

                  {activeStep === 1 && (
                    <div className="flex justify-center gap-x-6">
                      <button
                        onClick={handleBack}
                        className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 py-1 w-24 rounded text-navy-900 font-semibold"
                      >
                        {t('common.cancel')}
                      </button>
                      <ButtonPrimary title={t('common.next')} className="!py-1 !w-24 !rounded" onClick={handleNext} />
                    </div>
                  )}

                  {activeStep === 2 && (
                    <div className="flex justify-center gap-x-6">
                      <button
                        onClick={handleBack}
                        className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 py-1 w-24 rounded text-navy-900 font-semibold"
                      >
                        {t('common.cancel')}
                      </button>
                      <ButtonPrimary
                        title={t('common.confirm')}
                        className="!py-1 !w-24 !rounded"
                        onClick={handleSubmit(onSubmit)}
                      />
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </FormProvider>
      </Dialog>
    </Transition>
  )
}
